/**
 * nodeFilter.ts — the "main section" definition for the support console.
 *
 * MUST stay in sync with TreeSequenceBuilder.IsMainSection() on the .NET
 * server. Main sections are the structural nodes an agent highlights and a
 * guidance flow walks through — navigation, tabs, panels, grids, context
 * menus. Everything else (button, menuItem, link, field) is a DETAIL.
 *
 * The Highlight tree hides details by default (toggleable); the server
 * filters details out of intermediate flow steps. One predicate, two uses.
 */
import { UiTreeNode } from '../types';

const MAIN_SECTION_TYPES = new Set([
  'menu',
  'menu-group',   // legacy alias
  'nav-item',
  'tab',
  'panel',
  'page',
  'grid',
  'contextmenu',
  'context-menu', // legacy alias
  'row-menu',     // legacy alias for contextMenu
]);

/** True when a node's element type is a main section. */
export function isMainSection(elementType?: string): boolean {
  return MAIN_SECTION_TYPES.has((elementType ?? '').trim().toLowerCase());
}

/**
 * Filter a UiTreeNode tree down to main sections, but NEVER orphan a kept
 * node: if a detail node is hidden, its main-section descendants are lifted
 * up to the nearest kept ancestor so they stay reachable. Detail leaves with
 * no main-section descendant are dropped.
 *
 * A detail node is also KEPT if it has no main-section ancestor at all —
 * otherwise it (and its subtree) would vanish from the tree entirely and the
 * agent could never reach it. This mirrors the server's "always keep the
 * leaf" rule for the tree view.
 */
export function filterTreeToMainSections(nodes: UiTreeNode[]): UiTreeNode[] {
  // Returns the kept-and-rewired version of a node, or a list of promoted
  // descendants when the node itself is a hidden detail.
  function visit(node: UiTreeNode): UiTreeNode[] {
    const keptChildren = node.children.flatMap(visit);

    if (isMainSection(node.elementType)) {
      return [{ ...node, children: keptChildren }];
    }

    // Detail node: drop it but promote any kept descendants. If it has no
    // kept descendants AND is a leaf the agent might still need, keep it
    // only when it would otherwise be unreachable (handled by callers that
    // pass the toggle off). Here, default behaviour promotes descendants.
    return keptChildren;
  }

  return nodes.flatMap(visit);
}
